import { PASSWORD_CONFIG } from '@/constants/validation';
import { logger } from '@/utils/logger';

// localStorage 키
const GUEST_PASSWORD_KEY = 'olulo_guest_password';
const GUEST_PHONE_KEY = 'olulo_guest_phone';

/**
 * 저장된 게스트 비밀번호 조회
 * @returns 저장된 비밀번호 또는 null
 */
export function getSavedGuestPassword(): string | null {
  try {
    return localStorage.getItem(GUEST_PASSWORD_KEY);
  } catch {
    // 의도적으로 에러 무시: localStorage 접근 실패 시 null 반환 (비회원 주문에서 사용)
    return null;
  }
}

/**
 * 게스트 비밀번호를 localStorage에 저장
 * @param password - 저장할 비밀번호
 */
export function saveGuestPassword(password: string): void {
  try {
    localStorage.setItem(GUEST_PASSWORD_KEY, password);
  } catch (error) {
    logger.error('[Password] Failed to save guest password:', error);
  }
}

/**
 * 저장된 게스트 비밀번호 삭제
 */
export function clearSavedGuestPassword(): void {
  try {
    localStorage.removeItem(GUEST_PASSWORD_KEY);
  } catch (error) {
    logger.error('[Password] Failed to clear guest password:', error);
  }
}

/**
 * 저장된 게스트 전화번호 조회
 * @returns 저장된 전화번호 또는 null
 */
export function getSavedGuestPhone(): string | null {
  try {
    return localStorage.getItem(GUEST_PHONE_KEY);
  } catch {
    // 의도적으로 에러 무시: localStorage 접근 실패 시 null 반환 (비회원 주문에서 사용)
    return null;
  }
}

/**
 * 게스트 전화번호를 localStorage에 저장
 * @param phone - 저장할 전화번호
 */
export function saveGuestPhone(phone: string): void {
  try {
    localStorage.setItem(GUEST_PHONE_KEY, phone);
  } catch (error) {
    logger.error('[Password] Failed to save guest phone:', error);
  }
}

/**
 * 저장된 게스트 전화번호 삭제
 */
export function clearSavedGuestPhone(): void {
  try {
    localStorage.removeItem(GUEST_PHONE_KEY);
  } catch (error) {
    logger.error('[Password] Failed to clear guest phone:', error);
  }
}

/**
 * 저장된 게스트 credentials 조회
 * @returns 전화번호와 비밀번호 또는 null (둘 다 있어야 반환)
 */
export function getSavedGuestCredentials(): { phone: string; password: string } | null {
  const phone = getSavedGuestPhone();
  const password = getSavedGuestPassword();

  if (phone && password) {
    return { phone, password };
  }

  return null;
}

/**
 * 모든 게스트 credentials 삭제
 */
export function clearAllGuestCredentials(): void {
  clearSavedGuestPassword();
  clearSavedGuestPhone();
}

/**
 * 저장된 비밀번호가 있으면 반환, 없으면 새로 생성하여 저장 후 반환
 * @returns 게스트 비밀번호
 */
export function getOrCreateGuestPassword(): string {
  const saved = getSavedGuestPassword();
  if (saved) {
    return saved;
  }

  const newPassword = generateGuestPassword();
  saveGuestPassword(newPassword);
  return newPassword;
}

/**
 * 암호학적으로 안전한 게스트 비밀번호 생성
 *
 * 비회원 주문 시 주문 조회에 사용되는 임시 비밀번호를 생성합니다.
 * crypto.getRandomValues()를 사용하여 암호학적으로 안전한 난수를 생성하고,
 * Fisher-Yates 셔플 알고리즘으로 균등하게 섞습니다.
 *
 * 생성 규칙:
 * - 길이: PASSWORD_CONFIG.LENGTH (기본 8자)
 * - 최소 1개의 영문 소문자 포함
 * - 최소 1개의 숫자 포함
 *
 * @returns {string} 생성된 비밀번호 (예: "a3k9m2x7")
 *
 * @example
 * const password = generateGuestPassword();
 * console.log(password); // "k7m2a9x3"
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues
 */
export function generateGuestPassword(): string {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const allChars = lowercase + digits;

  /**
   * 암호학적으로 안전한 랜덤 문자 선택
   * @param chars - 선택할 문자열
   * @returns 랜덤하게 선택된 문자
   */
  const getRandomChar = (chars: string): string => {
    const randomValues = new Uint32Array(1);
    crypto.getRandomValues(randomValues);
    return chars[randomValues[0] % chars.length];
  };

  // 최소 요구사항 충족: 영문 소문자 1개 + 숫자 1개
  let password = '';
  for (let i = 0; i < PASSWORD_CONFIG.MIN_LETTER_COUNT; i++) {
    password += getRandomChar(lowercase);
  }
  for (let i = 0; i < PASSWORD_CONFIG.MIN_DIGIT_COUNT; i++) {
    password += getRandomChar(digits);
  }

  // 나머지 문자 채우기
  const remainingLength =
    PASSWORD_CONFIG.LENGTH - PASSWORD_CONFIG.MIN_LETTER_COUNT - PASSWORD_CONFIG.MIN_DIGIT_COUNT;
  for (let i = 0; i < remainingLength; i++) {
    password += getRandomChar(allChars);
  }

  // Fisher-Yates 셔플 알고리즘으로 균등하게 섞기
  const chars = password.split('');
  for (let i = chars.length - 1; i > 0; i--) {
    const randomValues = new Uint32Array(1);
    crypto.getRandomValues(randomValues);
    const j = randomValues[0] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars.join('');
}
